import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../../services/axiosInstance";
import {
  getRecurrentSubscription_service,
  getSubscriptionAddonPrices_service,
  getSubscriptionPlanPrices_service,
} from "../../modules/account/services/payments/subscriptions/recurrentSubscription.service";
import {
  useCheckoutStore,
  type CheckoutKind,
  type ICheckoutParams,
} from "../../modules/account/store/useCheckoutStore";
import { useCanPurchase } from "../../modules/account/hooks/useCanPurchase";
import { refreshPlanGate } from "../../hooks/usePlanGate";
import { planKeyOf } from "./featureCopy";

export interface PlanLimits {
  users?: number;
  comandaUsers?: number;
  cashboxes?: number;
  warehouses?: number;
  sites?: number;
}

export interface PlanOffer {
  loading: boolean;
  /** Precios NETOS (sin impuestos) por plan y periodicidad, del servidor. */
  plansNet: Record<string, Record<string, number>>;
  /** Precios NETOS mensuales de adicionales, del servidor. */
  addonsNet: Record<string, number>;
  /** Sufijo de impuesto que publica el servidor ("+ IGV"). */
  taxSuffix: string;
  /** Suscripción recurrente vigente (activa o en gracia, no cancelada). */
  hasActiveSub: boolean;
  /** Jerarquía del plan actual del local (2000/3000/4000; 0 si no se sabe). */
  hierarchy: number;
  limits: PlanLimits;
  /** Solo el propietario/administrador puede comprar (mismo criterio que Mi Cuenta). */
  canPurchase: boolean;
}

/** Precio neto mensual del plan (null si el catálogo no lo publica). */
export const monthlyNetOf = (offer: PlanOffer, planName: string): number | null => {
  const value = offer.plansNet?.[planKeyOf(planName)]?.mensual;
  return typeof value === "number" && value > 0 ? value : null;
};

/**
 * Checkout para pasar a `planName`, con la MISMA regla que el comparador de
 * planes de Mi Cuenta (AvailablePlans): con suscripción recurrente activa es un
 * "upgrade"; sin ella (prueba, pago manual) es contratar el plan ("subscribe").
 */
export const planCheckoutArgs = (
  hasActiveSub: boolean,
  planName: string
): [CheckoutKind, ICheckoutParams] => {
  const targetPlan = planKeyOf(planName);
  return hasActiveSub
    ? ["upgrade", { targetPlan }]
    : ["subscribe", { targetPlan, period: "mensual" }];
};

/**
 * Datos comerciales para vender un plan o un adicional FUERA de Mi Cuenta
 * (pantalla de bloqueo por plan y modal de límite). Todo sale del servidor:
 * precios del catálogo por país, suscripción y plan actual. Si algo falla no
 * rompe la pantalla: simplemente no se muestra el precio.
 */
export const usePlanOffer = (enabled = true) => {
  const canPurchase = useCanPurchase();
  const openCheckout = useCheckoutStore((s) => s.openCheckout);
  const [offer, setOffer] = useState<PlanOffer>({
    loading: enabled,
    plansNet: {},
    addonsNet: {},
    taxSuffix: "+ IGV",
    hasActiveSub: false,
    hierarchy: 0,
    limits: {},
    canPurchase,
  });

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    (async () => {
      const [plansRes, addonsRes, subRes, featuresRes] = await Promise.allSettled([
        getSubscriptionPlanPrices_service(),
        getSubscriptionAddonPrices_service(),
        getRecurrentSubscription_service(),
        axiosInstance.get("/api/v2/account/plan-features"),
      ]);
      if (!active) return;

      const plans =
        plansRes.status === "fulfilled" && plansRes.value.isSuccess
          ? plansRes.value.data
          : null;
      const addons =
        addonsRes.status === "fulfilled" && addonsRes.value.isSuccess
          ? addonsRes.value.data
          : null;
      const sub =
        subRes.status === "fulfilled" && subRes.value.isSuccess
          ? subRes.value.data
          : null;
      const features =
        featuresRes.status === "fulfilled"
          ? featuresRes.value?.data?.data ?? null
          : null;

      setOffer({
        loading: false,
        plansNet: plans?.plansNet ?? {},
        addonsNet: addons?.addonsNet ?? {},
        taxSuffix: plans?.taxSuffix || addons?.taxSuffix || "+ IGV",
        // Igual que useAvailablePlans: solo cuenta una suscripción no cancelada
        // y efectivamente activa.
        hasActiveSub: Boolean(
          sub && sub.status && sub.status !== "cancelada" && sub.active
        ),
        hierarchy: Number(features?.hierarchy ?? 0),
        limits: features?.limits ?? {},
        canPurchase,
      });
    })();
    return () => {
      active = false;
    };
  }, [enabled, canPurchase]);

  /** Abre el checkout del plan y, al pagar, reevalúa los gates. */
  const buyPlan = useCallback(
    (planName: string) => {
      const [kind, params] = planCheckoutArgs(offer.hasActiveSub, planName);
      openCheckout(kind, params, refreshPlanGate);
    },
    [offer.hasActiveSub, openCheckout]
  );

  /** Abre el checkout de 1 adicional suelto. */
  const buyAddon = useCallback(
    (type: string, onSuccess?: () => void) => {
      openCheckout("addon", { type, quantity: 1 }, onSuccess ?? refreshPlanGate);
    },
    [openCheckout]
  );

  return { offer, buyPlan, buyAddon };
};
